import { useCallback } from 'react';
import { Position, AnimationState } from '../types';

export const useAnimations = () => {
  // Helper function to create a basic animation
  const createAnimation = useCallback(
    (
      type: 'deal' | 'return' | 'move' | 'flip' | 'rotate',
      startPosition: Position, 
      endPosition: Position, 
      duration = 400,
      onComplete?: () => void
    ): AnimationState => {
      return {
        isAnimating: true,
        animationType: type,
        startPosition,
        endPosition,
        startTime: performance.now(),
        duration,
        onComplete
      };
    }, []
  );

  // Create a deal animation (card from deck to hand)
  const createDealAnimation = useCallback(
    (
      startPosition: Position, 
      endPosition: Position, 
      duration = 400,
      onComplete?: () => void
    ): AnimationState => {
      return createAnimation('deal', startPosition, endPosition, duration, onComplete);
    }, [createAnimation]
  );

  // Create a return animation (card back to deck)
  const createReturnAnimation = useCallback(
    (
      startPosition: Position, 
      endPosition: Position, 
      duration = 400,
      onComplete?: () => void
    ): AnimationState => {
      return createAnimation('return', startPosition, endPosition, duration, onComplete);
    }, [createAnimation]
  );

  // Create a flip animation
  const createFlipAnimation = useCallback(
    (onComplete?: () => void): AnimationState => {
      // For flip, we don't actually animate the position, but we use the animation system
      // to trigger the CSS transition. So we use dummy positions.
      const dummyPosition = { x: 0, y: 0 };
      return createAnimation('flip', dummyPosition, dummyPosition, 600, onComplete);
    }, [createAnimation]
  );

  // Create a rotation animation
  const createRotateAnimation = useCallback(
    (
      startRotation: number, 
      endRotation: number, 
      duration = 300,
      onComplete?: () => void
    ): AnimationState => {
      // For rotation, we store the rotation values in the x coordinate 
      // of the position objects
      return createAnimation(
        'rotate', 
        { x: startRotation, y: 0 }, 
        { x: endRotation, y: 0 }, 
        duration, 
        onComplete
      );
    }, [createAnimation]
  );

  // Create a move animation (card to new position)
  const createMoveAnimation = useCallback(
    (
      startPosition: Position, 
      endPosition: Position, 
      duration = 400,
      onComplete?: () => void
    ): AnimationState => {
      return createAnimation('move', startPosition, endPosition, duration, onComplete);
    }, [createAnimation]
  );

  return {
    createDealAnimation,
    createReturnAnimation,
    createFlipAnimation,
    createRotateAnimation,
    createMoveAnimation
  };
}; 